import {
  BadgeCheck,
  Banknote,
  Boxes,
  ClipboardCheck,
  FileCheck2,
  GitBranch,
  ListChecks,
  Package,
  Pencil,
  Plus,
  Rocket,
  Settings2,
  ShieldCheck,
  Trash2,
  Users
} from "lucide-react";
import { type FormEvent, type ReactNode, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Modal } from "../components/Modal";
import { api } from "../lib/api";
import type { TemplateWorkspace, WorkspaceItem } from "../lib/types";

type Kind =
  | "phase" | "activity" | "task" | "parameter" | "budget" | "resource"
  | "document" | "checklist" | "checklistItem" | "position" | "dependency"
  | "milestone" | "deliverable" | "provider" | "gate";

const endpoint: Record<Kind, string> = {
  phase: "phases",
  activity: "activities",
  task: "tasks",
  parameter: "parameters",
  budget: "budget-lines",
  resource: "resources",
  document: "required-documents",
  checklist: "checklists",
  checklistItem: "checklist-items",
  position: "positions",
  dependency: "task-dependencies",
  milestone: "milestones",
  deliverable: "deliverables",
  provider: "provider-needs",
  gate: "phase-gates"
};

function itemLabel(item: WorkspaceItem) {
  return String(
    item.nom || item.titre || item.libelle || item.objet || item.code || "Élément"
  );
}

function jsonValue(value: unknown, fallback: unknown = {}) {
  try {
    return JSON.stringify(value ?? fallback, null, 2);
  } catch {
    return JSON.stringify(fallback, null, 2);
  }
}

function parseJson(value: unknown, label: string, fallback: unknown = {}) {
  const raw = String(value ?? "").trim();
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    throw new Error(`${label} contient un JSON invalide.`);
  }
}

export function TemplateBuilderPage() {
  const { versionId } = useParams();
  const navigate = useNavigate();
  const [workspace, setWorkspace] = useState<TemplateWorkspace | null>(null);
  const [tab, setTab] = useState<
    "structure" | "planning" | "parameters" | "budget" | "resources" | "controls"
  >("structure");
  const [modal, setModal] = useState<{
    kind: Kind;
    item?: WorkspaceItem;
    checklistId?: number;
  } | null>(null);
  const [form, setForm] = useState<Record<string, string | boolean>>({});
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const draft = workspace?.version.statut === "DRAFT";

  async function load() {
    const data = await api.get<TemplateWorkspace>(
      `/api/v1/project-templates/versions/${versionId}/workspace/`
    );
    setWorkspace(data);
  }

  useEffect(() => { void load(); }, [versionId]);

  const phaseMap = useMemo(
    () => new Map((workspace?.phases || []).map((item) => [item.id, item])),
    [workspace]
  );
  const taskMap = useMemo(
    () => new Map((workspace?.tasks || []).map((item) => [item.id, item])),
    [workspace]
  );

  function open(
    kind: Kind,
    item?: WorkspaceItem,
    extra: { checklistId?: number; phase?: number } = {}
  ) {
    setError("");
    setForm({
      code: String(item?.code || ""),
      nom: String(item?.nom || item?.titre || item?.libelle || item?.objet || ""),
      description: String(item?.description || ""),
      phase: String(item?.phase || extra.phase || ""),
      activite: String(item?.activite || ""),
      tache: String(item?.tache || ""),
      parent: String(item?.parent || ""),
      poste_responsable: String(item?.poste_responsable || ""),
      ordre: String(item?.ordre ?? 0),
      duree_prevue_jours: String(item?.duree_prevue_jours ?? 0),
      effort_estime_heures: String(item?.effort_estime_heures ?? 0),
      poids: String(item?.poids ?? 1),
      priorite: String(item?.priorite || "NORMAL"),
      categorie: String(item?.categorie || ""),
      montant: String(item?.montant ?? item?.montant_indicatif ?? "0"),
      quantite: String(item?.quantite || "1"),
      unite: String(item?.unite || "unité"),
      type_ressource: String(item?.type_ressource || "MATERIAL"),
      type_valeur: String(item?.type_valeur || "TEXT"),
      choix: jsonValue(item?.choix, []),
      valeur_defaut: jsonValue(item?.valeur_defaut, null),
      obligatoire: item?.obligatoire !== false,
      condition_activation: jsonValue(item?.condition_activation, {}),
      valeurs_dynamiques: jsonValue(item?.valeurs_dynamiques, {}),
      metadata: jsonValue(item?.metadata, {}),
      capacite: String(item?.capacite || 1),
      allocation_recommandee_pct: String(item?.allocation_recommandee_pct || 100),
      competences_requises: jsonValue(item?.competences_requises, []),
      depend_de: String(item?.depend_de || ""),
      type_dependance: String(item?.type_dependance || "FS"),
      decalage_jours: String(item?.decalage_jours || 0),
      critique: Boolean(item?.critique),
      bloquante: Boolean(item?.bloquante),
      bloquant: Boolean(item?.bloquant),
      type_document: String(item?.type_document || "DOCUMENT"),
      type_livrable: String(item?.type_livrable || ""),
      validation_requise: Boolean(item?.validation_requise),
      type_reponse: String(item?.type_reponse || "BOOLEAN"),
      valeur_attendue: jsonValue(item?.valeur_attendue, null),
      exiger_taches: item?.exiger_taches !== false,
      exiger_checklists: item?.exiger_checklists !== false,
      exiger_documents: item?.exiger_documents !== false,
      exiger_livrables: item?.exiger_livrables !== false,
      exiger_jalons: item?.exiger_jalons !== false,
      approbation_requise: Boolean(item?.approbation_requise)
    });
    setModal({ kind, item, checklistId: extra.checklistId });
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!modal || !workspace) return;
    setError("");

    try {
      const kind = modal.kind;
      const version = workspace.version.id;
      const code = String(form.code || "").trim().toUpperCase();
      const name = String(form.nom || "").trim();
      const phase = form.phase ? Number(form.phase) : null;
      const activite = form.activite ? Number(form.activite) : null;
      const tache = form.tache ? Number(form.tache) : null;
      const condition_activation = parseJson(
        form.condition_activation, "Condition d’activation", {}
      );
      const valeurs_dynamiques = parseJson(
        form.valeurs_dynamiques, "Valeurs dynamiques", {}
      );
      const metadata = parseJson(form.metadata, "Métadonnées", {});

      let payload: Record<string, unknown>;

      switch (kind) {
        case "phase":
          payload = {
            version, code, nom: name, description: form.description || "",
            parent: form.parent ? Number(form.parent) : null,
            ordre: Number(form.ordre || 0),
            duree_prevue_jours: Number(form.duree_prevue_jours || 0),
            poids: form.poids || "1",
            budget_indicatif: form.montant || "0",
            obligatoire: Boolean(form.obligatoire),
            condition_activation, valeurs_dynamiques, metadata
          };
          break;
        case "activity":
          if (!phase) throw new Error("Une activité doit appartenir à une phase.");
          payload = {
            version, phase, code, nom: name, description: form.description || "",
            ordre: Number(form.ordre || 0),
            duree_prevue_jours: Number(form.duree_prevue_jours || 0),
            poids: form.poids || "1",
            obligatoire: Boolean(form.obligatoire),
            condition_activation, valeurs_dynamiques, metadata
          };
          break;
        case "task":
          if (!phase) throw new Error("Une tâche doit appartenir à une phase.");
          payload = {
            version, phase, activite,
            parent: form.parent ? Number(form.parent) : null,
            poste_responsable: form.poste_responsable
              ? Number(form.poste_responsable) : null,
            code, titre: name, description: form.description || "",
            ordre: Number(form.ordre || 0),
            duree_prevue_jours: Number(form.duree_prevue_jours || 0),
            effort_estime_heures: form.effort_estime_heures || "0",
            poids: form.poids || "1", priorite: form.priorite || "NORMAL",
            prestataire_autorise: true,
            obligatoire: Boolean(form.obligatoire),
            condition_activation, valeurs_dynamiques, metadata
          };
          break;
        case "parameter":
          payload = {
            version, code, libelle: name,
            type_valeur: form.type_valeur || "TEXT",
            obligatoire: Boolean(form.obligatoire),
            valeur_defaut: parseJson(form.valeur_defaut, "Valeur par défaut", null),
            choix: parseJson(form.choix, "Choix", []),
            ordre: Number(form.ordre || 0)
          };
          break;
        case "budget":
          payload = {
            version, phase, activite, tache, code,
            categorie: form.categorie || "GENERAL", libelle: name,
            montant: form.montant || "0", devise: "XOF",
            obligatoire: Boolean(form.obligatoire),
            condition_activation, valeurs_dynamiques
          };
          break;
        case "resource":
          payload = {
            version, phase, activite, tache,
            type_ressource: form.type_ressource || "MATERIAL",
            code, nom: name, quantite: form.quantite || "1",
            unite: form.unite || "unité", cout_indicatif: form.montant || "0",
            obligatoire: Boolean(form.obligatoire),
            condition_activation, valeurs_dynamiques
          };
          break;
        case "document":
          payload = {
            version, phase, activite, tache, code, nom: name,
            type_document: form.type_document || "DOCUMENT",
            obligatoire: Boolean(form.obligatoire),
            bloquant: Boolean(form.bloquant), condition_activation
          };
          break;
        case "checklist":
          payload = {
            version, phase, activite, tache, code, nom: name,
            bloquante: Boolean(form.bloquante),
            obligatoire: Boolean(form.obligatoire), condition_activation
          };
          break;
        case "checklistItem":
          if (!modal.checklistId && !modal.item?.checklist) {
            throw new Error("Checklist introuvable.");
          }
          payload = {
            checklist: modal.checklistId || modal.item?.checklist,
            code, libelle: name,
            type_reponse: form.type_reponse || "BOOLEAN",
            obligatoire: Boolean(form.obligatoire),
            bloquant: Boolean(form.bloquant),
            valeur_attendue: parseJson(form.valeur_attendue, "Valeur attendue", null),
            ordre: Number(form.ordre || 0)
          };
          break;
        case "position":
          payload = {
            version, phase, activite, code, nom: name,
            description: form.description || "",
            capacite: Number(form.capacite || 1),
            competences_requises: parseJson(
              form.competences_requises, "Compétences", []
            ),
            allocation_recommandee_pct:
              form.allocation_recommandee_pct || "100",
            obligatoire: Boolean(form.obligatoire),
            valeurs_dynamiques, metadata
          };
          break;
        case "dependency":
          if (!form.tache || !form.depend_de) {
            throw new Error("Sélectionnez la tâche et son prédécesseur.");
          }
          payload = {
            version,
            tache: Number(form.tache),
            depend_de: Number(form.depend_de),
            type_dependance: form.type_dependance || "FS",
            decalage_jours: Number(form.decalage_jours || 0)
          };
          break;
        case "milestone":
          payload = {
            version, phase, activite, code, nom: name,
            description: form.description || "",
            decalage_jours: Number(form.decalage_jours || 0),
            critique: Boolean(form.critique),
            obligatoire: Boolean(form.obligatoire),
            condition_activation
          };
          break;
        case "deliverable":
          payload = {
            version, phase, activite, tache, code, nom: name,
            description: form.description || "",
            type_livrable: form.type_livrable || "",
            obligatoire: Boolean(form.obligatoire),
            validation_requise: Boolean(form.validation_requise)
          };
          break;
        case "provider":
          payload = {
            version, phase, activite, code,
            categorie: form.categorie || "SERVICE",
            objet: name, montant_indicatif: form.montant || "0",
            obligatoire: Boolean(form.obligatoire),
            condition_activation, valeurs_dynamiques
          };
          break;
        case "gate":
          if (!phase) throw new Error("Une gate doit appartenir à une phase.");
          payload = {
            version, phase,
            exiger_taches: Boolean(form.exiger_taches),
            exiger_checklists: Boolean(form.exiger_checklists),
            exiger_documents: Boolean(form.exiger_documents),
            exiger_livrables: Boolean(form.exiger_livrables),
            exiger_jalons: Boolean(form.exiger_jalons),
            approbation_requise: Boolean(form.approbation_requise)
          };
          break;
      }

      const base = `/api/v1/project-templates/${endpoint[kind]}/`;
      if (modal.item) {
        await api.patch(`${base}${modal.item.id}/`, payload);
      } else {
        await api.post(base, payload);
      }
      setModal(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Enregistrement impossible.");
    }
  }

  async function remove(kind: Kind, item: WorkspaceItem) {
    if (!confirm(`Supprimer « ${itemLabel(item)} » ?`)) return;
    await api.delete(
      `/api/v1/project-templates/${endpoint[kind]}/${item.id}/`
    );
    await load();
  }

  async function validate() {
    try {
      const result = await api.post<{
        valid: boolean;
        counts: Record<string, number>;
      }>(
        `/api/v1/project-templates/versions/${versionId}/validate/`,
        {}
      );
      setMessage(
        `Validation OK · ${result.counts.phases} phases · ${result.counts.tasks} tâches`
      );
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Validation impossible.");
    }
  }

  async function publish() {
    await api.post(
      `/api/v1/project-templates/versions/${versionId}/publish/`,
      {}
    );
    await load();
    setMessage("Version publiée. Elle est désormais immuable.");
  }

  if (!workspace) {
    return <div className="p-10 text-center text-slate-500">Chargement du builder…</div>;
  }

  const tabs = [
    ["structure", "Structure", Boxes],
    ["planning", "Planning & rôles", GitBranch],
    ["parameters", "Paramètres", Settings2],
    ["budget", "Budget", Banknote],
    ["resources", "Ressources", Package],
    ["controls", "Contrôles", ClipboardCheck]
  ] as const;

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <button
            className="mb-3 text-sm font-semibold text-slate-500 hover:text-slate-900"
            onClick={() => navigate("/templates")}
          >
            ← Bibliothèque
          </button>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-black">{workspace.template.nom}</h1>
            <span className={`badge ${draft
              ? "bg-amber-100 text-amber-800"
              : "bg-emerald-100 text-emerald-800"}`}>
              v{workspace.version.numero_version} · {workspace.version.statut}
            </span>
          </div>
          <p className="mt-2 text-sm text-slate-500">
            {workspace.template.code} · {workspace.template.domaine || "GÉNÉRIQUE"}
          </p>
        </div>
        <div className="flex gap-2">
          <button className="btn-secondary" onClick={() => void validate()}>
            <BadgeCheck size={16} /> Valider
          </button>
          {draft && (
            <button className="btn-primary" onClick={() => void publish()}>
              <Rocket size={16} /> Publier la version
            </button>
          )}
        </div>
      </div>

      {message && (
        <div className="mt-4 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700">
          {message}
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-2 border-b border-slate-200 pb-3">
        {tabs.map(([key, label, Icon]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold ${tab === key
              ? "bg-slate-950 text-white"
              : "bg-white text-slate-600 hover:bg-slate-100"}`}
          >
            <Icon size={16} /> {label}
          </button>
        ))}
      </div>

      {tab === "structure" && (
        <div className="mt-6 space-y-5">
          <div className="flex justify-end gap-2">
            <button disabled={!draft} className="btn-secondary" onClick={() => open("activity")}>
              <Plus size={15} /> Activité
            </button>
            <button disabled={!draft} className="btn-secondary" onClick={() => open("task")}>
              <Plus size={15} /> Tâche
            </button>
            <button disabled={!draft} className="btn-primary" onClick={() => open("phase")}>
              <Plus size={15} /> Phase
            </button>
          </div>

          {workspace.phases.map((phase) => (
            <section key={phase.id} className="panel overflow-hidden">
              <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-5 py-4">
                <div>
                  <div className="text-xs font-bold uppercase tracking-wide text-slate-400">
                    Phase {Number(phase.ordre || 0) + 1}
                  </div>
                  <h2 className="mt-1 text-lg font-bold">{itemLabel(phase)}</h2>
                  <div className="mt-1 text-xs font-mono text-slate-400">{phase.code}</div>
                </div>
                {draft && (
                  <div className="flex gap-1">
                    <button className="rounded-lg p-2 hover:bg-white" onClick={() => open("phase", phase)}>
                      <Pencil size={16} />
                    </button>
                    <button className="rounded-lg p-2 text-red-600 hover:bg-red-50" onClick={() => void remove("phase", phase)}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                )}
              </div>

              <div className="grid gap-4 p-5 xl:grid-cols-2">
                {workspace.activities
                  .filter((activity) => activity.phase === phase.id)
                  .map((activity) => (
                    <div key={activity.id} className="rounded-xl border border-slate-200 p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-bold">{itemLabel(activity)}</div>
                          <div className="mt-1 text-xs font-mono text-slate-400">{activity.code}</div>
                        </div>
                        {draft && (
                          <button onClick={() => open("activity", activity)} className="rounded-lg p-2 hover:bg-slate-100">
                            <Pencil size={15} />
                          </button>
                        )}
                      </div>
                      <div className="mt-3 space-y-2">
                        {workspace.tasks
                          .filter((task) => task.phase === phase.id && task.activite === activity.id)
                          .map((task) => (
                            <TaskRow key={task.id} task={task} draft={draft} onEdit={() => open("task", task)} />
                          ))}
                      </div>
                    </div>
                  ))}

                {workspace.tasks
                  .filter((task) => task.phase === phase.id && !task.activite)
                  .map((task) => (
                    <TaskRow key={task.id} task={task} draft={draft} onEdit={() => open("task", task)} card />
                  ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {tab === "planning" && (
        <div className="grid gap-5 xl:grid-cols-2">
          <ListSection
            title="Postes projet"
            icon={<Users size={18} />}
            items={workspace.positions}
            draft={draft}
            onAdd={() => open("position")}
            onEdit={(item) => open("position", item)}
            onDelete={(item) => remove("position", item)}
          />
          <ListSection
            title="Jalons"
            icon={<BadgeCheck size={18} />}
            items={workspace.milestones}
            draft={draft}
            onAdd={() => open("milestone")}
            onEdit={(item) => open("milestone", item)}
            onDelete={(item) => remove("milestone", item)}
          />
          <section className="panel mt-6 p-5 xl:col-span-2">
            <div className="flex items-center justify-between">
              <h2 className="flex items-center gap-2 text-lg font-bold">
                <GitBranch size={18} /> Dépendances de tâches
              </h2>
              {draft && (
                <button className="btn-primary" onClick={() => open("dependency")}>
                  <Plus size={15} /> Dépendance
                </button>
              )}
            </div>
            <div className="mt-4 divide-y divide-slate-100">
              {workspace.dependencies.map((dep) => (
                <div key={dep.id} className="flex items-center justify-between py-3">
                  <div className="text-sm">
                    <strong>{itemLabel(taskMap.get(Number(dep.tache)) || { id: 0 })}</strong>
                    <span className="mx-2 text-slate-400">dépend de</span>
                    <strong>{itemLabel(taskMap.get(Number(dep.depend_de)) || { id: 0 })}</strong>
                    <span className="ml-2 text-xs text-slate-400">
                      {String(dep.type_dependance || "FS")} · {Number(dep.decalage_jours || 0)} j
                    </span>
                  </div>
                  {draft && (
                    <div className="flex gap-1">
                      <button className="rounded-lg p-2 hover:bg-slate-100" onClick={() => open("dependency", dep)}>
                        <Pencil size={15} />
                      </button>
                      <button className="rounded-lg p-2 text-red-600 hover:bg-red-50" onClick={() => void remove("dependency", dep)}>
                        <Trash2 size={15} />
                      </button>
                    </div>
                  )}
                </div>
              ))}
              {!workspace.dependencies.length && (
                <div className="py-8 text-center text-sm text-slate-500">Aucune dépendance.</div>
              )}
            </div>
          </section>
        </div>
      )}

      {tab === "parameters" && (
        <ListSection
          title="Paramètres du template"
          icon={<Settings2 size={18} />}
          items={workspace.parameters}
          draft={draft}
          onAdd={() => open("parameter")}
          onEdit={(item) => open("parameter", item)}
          onDelete={(item) => remove("parameter", item)}
        />
      )}

      {tab === "budget" && (
        <ListSection
          title="Lignes budgétaires"
          icon={<Banknote size={18} />}
          items={workspace.budget_lines}
          draft={draft}
          onAdd={() => open("budget")}
          onEdit={(item) => open("budget", item)}
          onDelete={(item) => remove("budget", item)}
        />
      )}

      {tab === "resources" && (
        <div className="grid gap-5 xl:grid-cols-2">
          <ListSection
            title="Ressources prévues"
            icon={<Package size={18} />}
            items={workspace.resources}
            draft={draft}
            onAdd={() => open("resource")}
            onEdit={(item) => open("resource", item)}
            onDelete={(item) => remove("resource", item)}
          />
          <ListSection
            title="Besoins prestataires"
            icon={<Users size={18} />}
            items={workspace.provider_needs}
            draft={draft}
            onAdd={() => open("provider")}
            onEdit={(item) => open("provider", item)}
            onDelete={(item) => remove("provider", item)}
          />
        </div>
      )}

      {tab === "controls" && (
        <div className="mt-6 grid gap-5 xl:grid-cols-2">
          <section className="panel p-5">
            <div className="flex items-center justify-between">
              <h2 className="flex items-center gap-2 text-lg font-bold">
                <ListChecks size={18} /> Checklists
              </h2>
              {draft && (
                <button className="btn-primary" onClick={() => open("checklist")}>
                  <Plus size={15} /> Checklist
                </button>
              )}
            </div>
            <div className="mt-4 space-y-3">
              {workspace.checklists.map((checklist) => {
                const items = Array.isArray(checklist.items)
                  ? (checklist.items as WorkspaceItem[])
                  : [];
                return (
                  <div key={checklist.id} className="rounded-xl border border-slate-200 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-bold">{itemLabel(checklist)}</div>
                        <div className="text-xs font-mono text-slate-400">{checklist.code}</div>
                      </div>
                      {draft && (
                        <div className="flex gap-1">
                          <button className="rounded-lg p-2 hover:bg-slate-100" onClick={() => open("checklistItem", undefined, { checklistId: checklist.id })}>
                            <Plus size={15} />
                          </button>
                          <button className="rounded-lg p-2 hover:bg-slate-100" onClick={() => open("checklist", checklist)}>
                            <Pencil size={15} />
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="mt-3 divide-y divide-slate-100 rounded-lg bg-slate-50 px-3">
                      {items.map((item) => (
                        <div key={item.id} className="flex items-center justify-between py-2 text-sm">
                          <span>{itemLabel(item)}</span>
                          {draft && (
                            <button className="p-1.5 text-slate-500" onClick={() => open("checklistItem", item, { checklistId: checklist.id })}>
                              <Pencil size={14} />
                            </button>
                          )}
                        </div>
                      ))}
                      {!items.length && <div className="py-3 text-xs text-slate-400">Aucun item.</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <ListSection
            title="Documents requis"
            icon={<FileCheck2 size={18} />}
            items={workspace.required_documents}
            draft={draft}
            onAdd={() => open("document")}
            onEdit={(item) => open("document", item)}
            onDelete={(item) => remove("document", item)}
          />
          <ListSection
            title="Livrables"
            icon={<ClipboardCheck size={18} />}
            items={workspace.deliverables}
            draft={draft}
            onAdd={() => open("deliverable")}
            onEdit={(item) => open("deliverable", item)}
            onDelete={(item) => remove("deliverable", item)}
          />
          <section className="panel mt-6 p-5">
            <div className="flex items-center justify-between">
              <h2 className="flex items-center gap-2 text-lg font-bold">
                <ShieldCheck size={18} /> Gates de phase
              </h2>
              {draft && (
                <button className="btn-primary" onClick={() => open("gate")}>
                  <Plus size={15} /> Gate
                </button>
              )}
            </div>
            <div className="mt-4 divide-y divide-slate-100">
              {workspace.phase_gates.map((gate) => (
                <div key={gate.id} className="flex items-center justify-between py-3">
                  <div>
                    <div className="font-semibold">
                      {itemLabel(phaseMap.get(Number(gate.phase)) || { id: 0 })}
                    </div>
                    <div className="mt-1 text-xs text-slate-400">
                      {gate.approbation_requise ? "Approbation requise" : "Contrôles automatiques"}
                    </div>
                  </div>
                  {draft && (
                    <div className="flex gap-1">
                      <button className="rounded-lg p-2 hover:bg-slate-100" onClick={() => open("gate", gate)}>
                        <Pencil size={15} />
                      </button>
                      <button className="rounded-lg p-2 text-red-600 hover:bg-red-50" onClick={() => void remove("gate", gate)}>
                        <Trash2 size={15} />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      <Modal
        open={Boolean(modal)}
        title={modal?.item ? "Modifier l’élément" : "Ajouter un élément"}
        onClose={() => setModal(null)}
        wide
      >
        <form onSubmit={save} className="space-y-5">
          {modal && !["dependency", "gate"].includes(modal.kind) && (
            <div className="grid gap-4 md:grid-cols-2">
              <label>
                <span className="label">Code</span>
                <input className="field" value={String(form.code || "")} onChange={(e) => setForm({ ...form, code: e.target.value })} required />
              </label>
              <label>
                <span className="label">
                  {modal.kind === "task"
                    ? "Titre"
                    : modal.kind === "provider"
                      ? "Objet"
                      : "Nom / libellé"}
                </span>
                <input className="field" value={String(form.nom || "")} onChange={(e) => setForm({ ...form, nom: e.target.value })} required />
              </label>
            </div>
          )}

          {modal && [
            "activity", "task", "budget", "resource", "document", "checklist",
            "position", "milestone", "deliverable", "provider", "gate"
          ].includes(modal.kind) && (
            <label>
              <span className="label">Phase</span>
              <select
                className="field"
                value={String(form.phase || "")}
                onChange={(e) => setForm({ ...form, phase: e.target.value })}
                required={["activity", "task", "gate"].includes(modal.kind)}
              >
                <option value="">Aucune / globale</option>
                {workspace.phases.map((phase) => (
                  <option key={phase.id} value={phase.id}>{itemLabel(phase)}</option>
                ))}
              </select>
            </label>
          )}

          {modal && [
            "task", "budget", "resource", "document", "checklist",
            "position", "milestone", "deliverable", "provider"
          ].includes(modal.kind) && (
            <label>
              <span className="label">Activité</span>
              <select className="field" value={String(form.activite || "")} onChange={(e) => setForm({ ...form, activite: e.target.value })}>
                <option value="">Aucune</option>
                {workspace.activities
                  .filter((activity) => !form.phase || String(activity.phase) === String(form.phase))
                  .map((activity) => (
                    <option key={activity.id} value={activity.id}>{itemLabel(activity)}</option>
                  ))}
              </select>
            </label>
          )}

          {modal && ["budget", "resource", "document", "checklist", "deliverable"].includes(modal.kind) && (
            <label>
              <span className="label">Tâche</span>
              <select className="field" value={String(form.tache || "")} onChange={(e) => setForm({ ...form, tache: e.target.value })}>
                <option value="">Aucune</option>
                {workspace.tasks
                  .filter((task) => !form.phase || String(task.phase) === String(form.phase))
                  .map((task) => (
                    <option key={task.id} value={task.id}>{itemLabel(task)}</option>
                  ))}
              </select>
            </label>
          )}

          {modal && ["phase", "activity", "task"].includes(modal.kind) && (
            <div className="grid gap-3 md:grid-cols-4">
              <NumberField label="Ordre" value={form.ordre} onChange={(value) => setForm({ ...form, ordre: value })} />
              <NumberField label="Durée (jours)" value={form.duree_prevue_jours} onChange={(value) => setForm({ ...form, duree_prevue_jours: value })} />
              <NumberField label="Poids" value={form.poids} onChange={(value) => setForm({ ...form, poids: value })} step="0.01" />
              {modal.kind === "task" && (
                <NumberField label="Effort (h)" value={form.effort_estime_heures} onChange={(value) => setForm({ ...form, effort_estime_heures: value })} step="0.01" />
              )}
            </div>
          )}

          {modal?.kind === "task" && (
            <div className="grid gap-3 md:grid-cols-3">
              <label>
                <span className="label">Parent</span>
                <select className="field" value={String(form.parent || "")} onChange={(e) => setForm({ ...form, parent: e.target.value })}>
                  <option value="">Aucun</option>
                  {workspace.tasks.map((task) => <option key={task.id} value={task.id}>{itemLabel(task)}</option>)}
                </select>
              </label>
              <label>
                <span className="label">Poste responsable</span>
                <select className="field" value={String(form.poste_responsable || "")} onChange={(e) => setForm({ ...form, poste_responsable: e.target.value })}>
                  <option value="">Aucun</option>
                  {workspace.positions.map((position) => <option key={position.id} value={position.id}>{itemLabel(position)}</option>)}
                </select>
              </label>
              <label>
                <span className="label">Priorité</span>
                <select className="field" value={String(form.priorite || "NORMAL")} onChange={(e) => setForm({ ...form, priorite: e.target.value })}>
                  {["LOW", "NORMAL", "HIGH", "CRITICAL"].map((value) => <option key={value}>{value}</option>)}
                </select>
              </label>
            </div>
          )}

          {modal?.kind === "parameter" && (
            <div className="grid gap-3 md:grid-cols-3">
              <label>
                <span className="label">Type</span>
                <select className="field" value={String(form.type_valeur || "TEXT")} onChange={(e) => setForm({ ...form, type_valeur: e.target.value })}>
                  {["TEXT", "NUMBER", "BOOLEAN", "DATE", "CHOICE"].map((value) => <option key={value}>{value}</option>)}
                </select>
              </label>
              <NumberField label="Ordre" value={form.ordre} onChange={(value) => setForm({ ...form, ordre: value })} />
              <label>
                <span className="label">Choix (JSON)</span>
                <input className="field font-mono" value={String(form.choix || "[]")} onChange={(e) => setForm({ ...form, choix: e.target.value })} />
              </label>
              <label className="md:col-span-3">
                <span className="label">Valeur par défaut (JSON)</span>
                <input className="field font-mono" value={String(form.valeur_defaut || "null")} onChange={(e) => setForm({ ...form, valeur_defaut: e.target.value })} />
              </label>
            </div>
          )}

          {modal?.kind === "budget" && (
            <div className="grid gap-3 md:grid-cols-2">
              <label><span className="label">Catégorie</span><input className="field" value={String(form.categorie || "")} onChange={(e) => setForm({ ...form, categorie: e.target.value })} /></label>
              <NumberField label="Montant XOF" value={form.montant} onChange={(value) => setForm({ ...form, montant: value })} step="0.01" />
            </div>
          )}

          {modal?.kind === "resource" && (
            <div className="grid gap-3 md:grid-cols-4">
              <label><span className="label">Type</span><select className="field" value={String(form.type_ressource || "MATERIAL")} onChange={(e) => setForm({ ...form, type_ressource: e.target.value })}>{["MATERIAL","EQUIPMENT","VEHICLE","SERVICE","SPACE"].map((value)=><option key={value}>{value}</option>)}</select></label>
              <NumberField label="Quantité" value={form.quantite} onChange={(value) => setForm({ ...form, quantite: value })} step="0.001" />
              <label><span className="label">Unité</span><input className="field" value={String(form.unite || "")} onChange={(e) => setForm({ ...form, unite: e.target.value })}/></label>
              <NumberField label="Coût indicatif" value={form.montant} onChange={(value) => setForm({ ...form, montant: value })} step="0.01" />
            </div>
          )}

          {modal?.kind === "position" && (
            <div className="grid gap-3 md:grid-cols-2">
              <NumberField label="Capacité" value={form.capacite} onChange={(value) => setForm({ ...form, capacite: value })} />
              <NumberField label="Allocation recommandée %" value={form.allocation_recommandee_pct} onChange={(value) => setForm({ ...form, allocation_recommandee_pct: value })} step="0.01" />
              <label className="md:col-span-2"><span className="label">Compétences requises (JSON)</span><textarea className="field min-h-24 font-mono" value={String(form.competences_requises || "[]")} onChange={(e) => setForm({ ...form, competences_requises: e.target.value })}/></label>
            </div>
          )}

          {modal?.kind === "dependency" && (
            <div className="grid gap-3 md:grid-cols-2">
              <label><span className="label">Tâche</span><select required className="field" value={String(form.tache || "")} onChange={(e)=>setForm({...form,tache:e.target.value})}><option value="">Sélectionner</option>{workspace.tasks.map((task)=><option key={task.id} value={task.id}>{itemLabel(task)}</option>)}</select></label>
              <label><span className="label">Dépend de</span><select required className="field" value={String(form.depend_de || "")} onChange={(e)=>setForm({...form,depend_de:e.target.value})}><option value="">Sélectionner</option>{workspace.tasks.map((task)=><option key={task.id} value={task.id}>{itemLabel(task)}</option>)}</select></label>
              <label><span className="label">Type</span><select className="field" value={String(form.type_dependance || "FS")} onChange={(e)=>setForm({...form,type_dependance:e.target.value})}>{["FS","SS","FF","SF"].map((value)=><option key={value}>{value}</option>)}</select></label>
              <NumberField label="Décalage jours" value={form.decalage_jours} onChange={(value)=>setForm({...form,decalage_jours:value})}/>
            </div>
          )}

          {modal?.kind === "milestone" && (
            <NumberField label="Décalage depuis le début (jours)" value={form.decalage_jours} onChange={(value)=>setForm({...form,decalage_jours:value})}/>
          )}

          {modal?.kind === "provider" && (
            <div className="grid gap-3 md:grid-cols-2">
              <label><span className="label">Catégorie</span><input className="field" value={String(form.categorie||"")} onChange={(e)=>setForm({...form,categorie:e.target.value})}/></label>
              <NumberField label="Montant indicatif" value={form.montant} onChange={(value)=>setForm({...form,montant:value})} step="0.01"/>
            </div>
          )}

          {modal?.kind === "document" && (
            <label><span className="label">Type document</span><input className="field" value={String(form.type_document||"DOCUMENT")} onChange={(e)=>setForm({...form,type_document:e.target.value})}/></label>
          )}

          {modal?.kind === "deliverable" && (
            <label><span className="label">Type livrable</span><input className="field" value={String(form.type_livrable||"")} onChange={(e)=>setForm({...form,type_livrable:e.target.value})}/></label>
          )}

          {modal?.kind === "checklistItem" && (
            <div className="grid gap-3 md:grid-cols-2">
              <label><span className="label">Type réponse</span><select className="field" value={String(form.type_reponse||"BOOLEAN")} onChange={(e)=>setForm({...form,type_reponse:e.target.value})}>{["BOOLEAN","TEXT","NUMBER","DATE","PHOTO","DOCUMENT"].map((value)=><option key={value}>{value}</option>)}</select></label>
              <NumberField label="Ordre" value={form.ordre} onChange={(value)=>setForm({...form,ordre:value})}/>
              <label className="md:col-span-2"><span className="label">Valeur attendue (JSON)</span><input className="field font-mono" value={String(form.valeur_attendue||"null")} onChange={(e)=>setForm({...form,valeur_attendue:e.target.value})}/></label>
            </div>
          )}

          {modal?.kind === "gate" && (
            <div className="grid gap-3 sm:grid-cols-2">
              {[
                ["exiger_taches","Tâches terminées"],
                ["exiger_checklists","Checklists complètes"],
                ["exiger_documents","Documents présents"],
                ["exiger_livrables","Livrables validés"],
                ["exiger_jalons","Jalons atteints"],
                ["approbation_requise","Approbation explicite"]
              ].map(([key,label])=>(
                <label key={key} className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 text-sm font-semibold">
                  <input type="checkbox" checked={Boolean(form[key])} onChange={(e)=>setForm({...form,[key]:e.target.checked})}/>
                  {label}
                </label>
              ))}
            </div>
          )}

          {modal && [
            "parameter","budget","resource","document","checklist","checklistItem",
            "position","milestone","deliverable","provider"
          ].includes(modal.kind) && (
            <div className="flex flex-wrap gap-5">
              <CheckField label="Obligatoire" checked={Boolean(form.obligatoire)} onChange={(value)=>setForm({...form,obligatoire:value})}/>
              {["document","checklistItem"].includes(modal.kind) && <CheckField label="Bloquant" checked={Boolean(form.bloquant)} onChange={(value)=>setForm({...form,bloquant:value})}/>}
              {modal.kind==="checklist" && <CheckField label="Checklist bloquante" checked={Boolean(form.bloquante)} onChange={(value)=>setForm({...form,bloquante:value})}/>}
              {modal.kind==="milestone" && <CheckField label="Critique" checked={Boolean(form.critique)} onChange={(value)=>setForm({...form,critique:value})}/>}
              {modal.kind==="deliverable" && <CheckField label="Validation requise" checked={Boolean(form.validation_requise)} onChange={(value)=>setForm({...form,validation_requise:value})}/>}
            </div>
          )}

          {modal && [
            "phase","activity","task","budget","resource","document","checklist",
            "milestone","provider"
          ].includes(modal.kind) && (
            <div className="grid gap-4 lg:grid-cols-2">
              <label><span className="label">Condition d’activation (JSON)</span><textarea className="field min-h-28 font-mono text-xs" value={String(form.condition_activation||"{}")} onChange={(e)=>setForm({...form,condition_activation:e.target.value})}/></label>
              {["phase","activity","task","budget","resource","provider"].includes(modal.kind) && (
                <label><span className="label">Valeurs dynamiques (JSON)</span><textarea className="field min-h-28 font-mono text-xs" value={String(form.valeurs_dynamiques||"{}")} onChange={(e)=>setForm({...form,valeurs_dynamiques:e.target.value})}/></label>
              )}
            </div>
          )}

          {error && <div className="rounded-xl bg-red-50 p-3 text-sm text-red-700">{error}</div>}

          <div className="flex justify-end gap-2">
            <button type="button" className="btn-secondary" onClick={() => setModal(null)}>Annuler</button>
            <button className="btn-primary">Enregistrer</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function TaskRow({
  task, draft, onEdit, card = false
}: {
  task: WorkspaceItem;
  draft: boolean;
  onEdit: () => void;
  card?: boolean;
}) {
  return (
    <div className={`flex items-center justify-between ${card
      ? "rounded-xl border border-slate-200 p-4"
      : "rounded-lg bg-slate-50 px-3 py-2"}`}>
      <div>
        <div className="text-sm font-semibold">{itemLabel(task)}</div>
        <div className="text-xs text-slate-400">
          {String(task.code || "—")} · {Number(task.duree_prevue_jours || 0)} j
        </div>
      </div>
      {draft && (
        <button onClick={onEdit} className="p-1.5 text-slate-500">
          <Pencil size={14} />
        </button>
      )}
    </div>
  );
}

function ListSection({
  title, icon, items, draft, onAdd, onEdit, onDelete
}: {
  title: string;
  icon: ReactNode;
  items: WorkspaceItem[];
  draft: boolean;
  onAdd: () => void;
  onEdit: (item: WorkspaceItem) => void;
  onDelete: (item: WorkspaceItem) => void | Promise<void>;
}) {
  return (
    <section className="panel mt-6 p-5">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-lg font-bold">{icon}{title}</h2>
        {draft && (
          <button className="btn-primary" onClick={onAdd}>
            <Plus size={15} /> Ajouter
          </button>
        )}
      </div>
      <div className="mt-4 divide-y divide-slate-100">
        {items.map((item) => (
          <div key={item.id} className="flex items-center justify-between py-3">
            <div>
              <div className="font-semibold">{itemLabel(item)}</div>
              <div className="mt-1 text-xs font-mono text-slate-400">
                {String(item.code || item.categorie || "—")}
              </div>
            </div>
            {draft && (
              <div className="flex gap-1">
                <button className="rounded-lg p-2 hover:bg-slate-100" onClick={() => onEdit(item)}>
                  <Pencil size={15} />
                </button>
                <button className="rounded-lg p-2 text-red-600 hover:bg-red-50" onClick={() => void onDelete(item)}>
                  <Trash2 size={15} />
                </button>
              </div>
            )}
          </div>
        ))}
        {!items.length && (
          <div className="py-8 text-center text-sm text-slate-500">Aucun élément.</div>
        )}
      </div>
    </section>
  );
}

function NumberField({
  label, value, onChange, step = "1"
}: {
  label: string;
  value: string | boolean | undefined;
  onChange: (value: string) => void;
  step?: string;
}) {
  return (
    <label>
      <span className="label">{label}</span>
      <input
        className="field"
        type="number"
        step={step}
        value={String(value ?? "0")}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function CheckField({
  label, checked, onChange
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      {label}
    </label>
  );
}
